/**
 * Helper function to parse MongoDB date format
 * @param {Object|String|Date} dateField - Date in various formats
 * @returns {Date|String} Parsed date or empty string
 */
export const parseMongoDate = (dateField) => {
  console.log("parseMongoDate input:", dateField);

  if (!dateField) {
    console.log("parseMongoDate: no date field, returning empty string");
    return "";
  }

  // Handle MongoDB $date format
  if (dateField.$date) {
    const parsedDate = new Date(dateField.$date);
    console.log("parseMongoDate: parsed $date format:", parsedDate);
    return parsedDate;
  }

  // Handle regular date string
  if (typeof dateField === "string") {
    const parsedDate = new Date(dateField);
    console.log("parseMongoDate: parsed string format:", parsedDate);
    return parsedDate;
  }

  // Handle Date object
  if (dateField instanceof Date) {
    console.log("parseMongoDate: already Date object:", dateField);
    return dateField;
  }

  console.log("parseMongoDate: unknown format, returning empty string");
  return "";
};

/**
 * Get status color based on job status
 * @param {String} status - Job status
 * @returns {String} Color name
 */
export const getStatusColor = (status) => {
  switch (status) {
    case "Completed":
    case "completed":
    case "Complete":
      return "success";

    case "processing":
    case "In Progress":
    case "completed_with_errors":
      return "warning";

    // Parked by an operator: not a failure and not progress, so it gets its own
    // colour rather than sitting in the grey default with "pending".
    case "hold":
      return "warning";

    // Part states: waiting for another try / stopped on purpose.
    case "retrying":
    case "stopped":
      return "warning";

    case "failed":
    case "failed_login_form":
    case "failed_post_submission":
    case "failed_validation":
    case "failed_duplicate":
    case "error":
    case "Incomplete":
      return "error";

    // A part an edit dropped: neither done nor failed.
    case "skipped":
    case "cancelled":
      return "default";

    default:
      return "default";
  }
};

/**
 * Format a policy's creation stamp for the list table.
 *
 * Mongo hands these over as an ISO string, a { $date } wrapper, or a Date —
 * parseMongoDate already normalises all three, so this only has to deal with
 * the "no usable date" case.
 *
 * Returns { date, time } so the caller can stack them on two lines instead of
 * spending a whole column's width on one long string.
 */
export const formatCreatedAt = (dateField) => {
  const parsed = parseMongoDate(dateField);
  if (!parsed || isNaN(new Date(parsed).getTime())) {
    return { date: "-", time: "" };
  }
  const d = new Date(parsed);
  const pad = (n) => String(n).padStart(2, "0");
  const hours = d.getHours();
  const hour12 = hours % 12 || 12;
  return {
    date: `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`,
    time: `${pad(hour12)}:${pad(d.getMinutes())} ${hours < 12 ? "AM" : "PM"}`,
  };
};

/**
 * Rupee amount in Indian digit grouping (1,20,450.00).
 *
 * Returns "-" for anything that is not a real number, so an unpriced policy
 * reads as blank rather than as a genuine ₹0.
 */
export const formatCurrency = (value, { decimals = 2 } = {}) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "-";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

/**
 * A readable name for a job status, for a status chip.
 *
 * Deliberately more precise than the list page's labels, which flatten every
 * failure to "failed" so they read well inside a sentence. On a detail page
 * that flattening hides the distinction that matters most: a policy that
 * failed AFTER submission may already have been charged, and must not look
 * like one that merely failed to log in.
 */
export const jobStatusLabel = (status) => {
  const labels = {
    pending: "Waiting to start",
    hold: "On hold",
    processing: "In progress",
    completed: "Completed",
    Complete: "Completed",
    completed_with_errors: "Completed with errors",
    failed: "Failed",
    failed_login_form: "Failed at login",
    failed_post_submission: "Failed after submission",
    failed_validation: "Failed validation",
    failed_duplicate: "Duplicate",
    error: "Failed",
  };
  if (labels[status]) return labels[status];
  // Anything unmapped is still shown, just tidied: "some_new_status" reads as
  // "Some new status" rather than as a database value.
  const raw = String(status || "").trim();
  if (!raw) return "Waiting to start";
  const spaced = raw.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};
